/**
 * Zod 4 compatibility shim preserving zod 3 default error message text.
 *
 * Zod 4 changed default issue messages and issue shapes. Messages reach a
 * human in client-side validation (survey-kit renderers via `formatIssues`,
 * admin Controls mode via `stepZodSchemas`) and in admin-api, where the
 * creative import preflight builds each `reason` from `issue.message` for
 * the review table. This module installs a global `customError` map
 * emitting the zod 3 default-locale messages. Schema-level `message:` /
 * `error:` params still take precedence, same as zod 3.
 *
 * `error.format()` output shape is unchanged in zod 4, so with the error
 * map installed `.format()` callers need no changes.
 */
import { z } from "zod";

interface V4Issue {
  code: string;
  message: string;
  input?: unknown;
  expected?: unknown;
  values?: unknown[];
  keys?: PropertyKey[];
  options?: unknown[];
  note?: string;
  format?: string;
  origin?: string;
  minimum?: unknown;
  maximum?: unknown;
  inclusive?: boolean;
  multipleOf?: unknown;
}

const joinValues = (values: unknown[], separator = " | "): string =>
  values.map((v) => (typeof v === "string" ? `'${v}'` : String(v))).join(separator);

const parsedType = (data: unknown): string => {
  switch (typeof data) {
    case "string":
      return "string";
    case "number":
      return Number.isNaN(data) ? "nan" : "number";
    case "boolean":
      return "boolean";
    case "function":
      return "function";
    case "bigint":
      return "bigint";
    case "symbol":
      return "symbol";
    case "object":
      if (Array.isArray(data)) return "array";
      if (data === null) return "null";
      if (data instanceof Map) return "map";
      if (data instanceof Set) return "set";
      if (data instanceof Date) return "date";
      return "object";
    case "undefined":
      return "undefined";
    default:
      return "unknown";
  }
};

const v3Expected = (expected: unknown): string => {
  const e = String(expected);
  if (e === "int") return "integer";
  if (e === "record") return "object";
  if (e === "tuple") return "array";
  return e;
};

const v3Received = (issue: V4Issue): string => {
  const input = issue.input;
  if (issue.expected === "int" && typeof input === "number") {
    return "float";
  }
  return parsedType(input);
};

const isEnumIssue = (issue: V4Issue): boolean =>
  Array.isArray(issue.values) && issue.values.length > 1;

const tooSmallMessage = (issue: V4Issue): string => {
  const minimum = issue.minimum;
  const inclusive = issue.inclusive;
  switch (issue.origin) {
    case "array":
      return `Array must contain ${inclusive ? `at least ${String(minimum)}` : `more than ${String(minimum)}`} element(s)`;
    case "string":
      return `String must contain ${inclusive ? `at least ${String(minimum)}` : `over ${String(minimum)}`} character(s)`;
    case "number":
      return `Number must be ${inclusive ? "greater than or equal to " : "greater than "}${String(minimum)}`;
    case "date":
      return `Date must be ${inclusive ? "greater than or equal to " : "greater than "}${new Date(Number(minimum)).toString()}`;
    default:
      return "Invalid input";
  }
};

const tooBigMessage = (issue: V4Issue): string => {
  const maximum = issue.maximum;
  const inclusive = issue.inclusive;
  switch (issue.origin) {
    case "array":
      return `Array must contain ${inclusive ? `at most ${String(maximum)}` : `less than ${String(maximum)}`} element(s)`;
    case "string":
      return `String must contain ${inclusive ? `at most ${String(maximum)}` : `under ${String(maximum)}`} character(s)`;
    case "number":
    case "bigint":
      return `Number must be ${inclusive ? "less than or equal to " : "less than "}${String(maximum)}`;
    case "date":
      return `Date must be ${inclusive ? "smaller than or equal to " : "smaller than "}${new Date(Number(maximum)).toString()}`;
    default:
      return "Invalid input";
  }
};

const invalidTypeMessage = (issue: V4Issue): string => {
  const received = v3Received(issue);
  if (received === "undefined") return "Required";
  return `Expected ${v3Expected(issue.expected)}, received ${received}`;
};

const invalidValueMessage = (issue: V4Issue): string => {
  if (isEnumIssue(issue)) {
    const input = issue.input;
    if (typeof input !== "string") {
      if (input === undefined) return "Required";
      return `Expected ${joinValues(issue.values ?? [])}, received ${parsedType(input)}`;
    }
    return `Invalid enum value. Expected ${joinValues(issue.values ?? [])}, received '${input}'`;
  }
  return `Invalid literal value, expected ${JSON.stringify(issue.values?.[0])}`;
};

const v3Message = (issue: V4Issue): string | undefined => {
  switch (issue.code) {
    case "invalid_type":
      return invalidTypeMessage(issue);
    case "invalid_value":
      return invalidValueMessage(issue);
    case "unrecognized_keys":
      return `Unrecognized key(s) in object: ${joinValues(issue.keys ?? [], ", ")}`;
    case "invalid_union":
      if (issue.note === "No matching discriminator") {
        return `Invalid discriminator value. Expected ${joinValues(issue.options ?? [])}`;
      }
      return "Invalid input";
    case "invalid_format":
      if (issue.format === "regex") return "Invalid";
      return `Invalid ${issue.format}`;
    case "too_small":
      return tooSmallMessage(issue);
    case "too_big":
      return tooBigMessage(issue);
    case "not_multiple_of":
      return `Number must be a multiple of ${String(issue.multipleOf)}`;
    default:
      return undefined;
  }
};

let installed = false;

export const installZod3ErrorMap = (): void => {
  if (installed) return;
  installed = true;
  z.config({ customError: (issue) => v3Message(issue as V4Issue) });
};
